import React, { useCallback } from 'react'
import styled from '@emotion/styled'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBookBlank, faDashboard, faDatabase, faFaceExplode, faKey, faLink, faMagnifyingGlass, faPersonRunningFast, faRightFromBracket, faSquareTerminal, faTricycle } from '@fortawesome/pro-regular-svg-icons'
import { useAppSelector, useAppDispatch } from '../../../redux/hooks'
import { Theme } from '@emotion/react'
import { getApiDomain, getBuildNumber, getCopyright, getJsBundle } from '../../../lib/appInfo'
import { UserRole } from '@fusion-advantage/backcompat'
import { canViewAdhocEncounters, proposeOpenLog, proposeOpenStorybook, proposeRequestApiToken, proposeSplode, RootRouteContentType } from '@fusion-advantage/formlib'
import { AppModal, pushAppModal, setAppContent, setSimulateOffline, toggleTrainingMode } from '../../../redux/appSlice'
import { selectDashboardDisplayIdentity } from '../../../redux/dashboardDataSlice'
import { splode } from '../../../redux/thunk/splode'
import { logout } from '../../../redux/thunk/authThunk'
import { appSync } from '../../../redux/syncthunk/syncThunk'
import { Capacitor } from '@capacitor/core'
import { SyncType } from '../../../redux/syncthunk/SyncConfig'
import { saveAndReturnToDashboard } from '../../../redux/thunk/renderInstanceStore'
import { selectOpIsBlockingEncounterMgmt } from '../../../redux/selectDashboardLoading'
import { selectIsPreviewMode } from '../../../redux/selectPreview'

const AppSideNavRoot = styled.div(({theme}) => ({
	width: `${820*.38}px`,
	maxWidth: '100vw',
	height: `calc(100vh - ${theme.statusBarHeightPx}px)`,
	display: 'flex',
	flexDirection: 'column',
	backgroundColor: theme.colors.orangeMain,
	borderBottomRightRadius: '20px',
	color: 'white',
	// padding: theme.spacing(2, 2.5),
}))

const AppSideNavContent = styled.div(({theme}) => ({
	flex: 1,
	display: 'flex',
	flexDirection: 'row',
	columnGap: theme.spacing(2),
	'> svg': {
		fontSize: '18px',
	},
	padding: theme.spacing(8, 2.5, 2, 7.5)
}))

const NavList = styled.ul(({theme}) => ({
	listStyle: 'none',
	marginLeft: 0,
	paddingLeft: 0,
	fontSize: '18px',
	'> li': {
		padding: theme.spacing(2, 0),
	},
}))

const NavItemButton = styled.button({
	all: 'unset',
	cursor: 'pointer',
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'flex-start',
})

interface NavItemButtonIconProps {
	show?: boolean
}

const NavItemButtonIcon = styled.div<NavItemButtonIconProps>(({theme, show = true}) => ({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	width: '24px',
	marginRight: theme.spacing(2),
	marginTop: '2px',
	visibility: show ? 'visible' : 'hidden',
}))

const AppSideNavFooter = styled.div(({theme}) => ({
	padding: theme.spacing(2, 2.5, 2, 7.5),
	fontSize: '.825rem',
}))

const L4AppSideNav = React.forwardRef<HTMLDivElement>((props, ref) => {
	const displayIdentity = useAppSelector(selectDashboardDisplayIdentity)
	const authedIdentity = useAppSelector(state => state.dashboardData.authedIdentity)
	const isTrainingMode = useAppSelector(state => state.app.isTrainingMode)
	const isPreviewMode = useAppSelector(selectIsPreviewMode)
	const simulateOffline = useAppSelector(state => state.app.simulateOffline)
	const env = useAppSelector(state => state.app.environment)
	const contentType = useAppSelector(state => state.app.content.type)
	const branch = useAppSelector(state => state.render.form?.export?.Form.Encounter.BranchGUID)
	const renderer = useAppSelector(state => state.app.renderer)
	const opIsBlockingEncounterMgmt = useAppSelector(selectOpIsBlockingEncounterMgmt)
	const dispatch = useAppDispatch()

	const handleDashboardClick = useCallback(() => {
		dispatch(saveAndReturnToDashboard())
	}, [dispatch])

	const handleTrainingModeClick = useCallback(() => {
		if(opIsBlockingEncounterMgmt) {
			alert('A sync is currently in progress; please try again when complete.')
			return
		}
		dispatch(toggleTrainingMode())
		dispatch(saveAndReturnToDashboard())
		dispatch(appSync({
			type: SyncType.Complete,
		}))
	}, [opIsBlockingEncounterMgmt, dispatch])

	const handleManageEncountersClick = useCallback(() => {
		dispatch(pushAppModal(AppModal.ManageEncounters))
	}, [dispatch])

	const handleGenerateMeetingLinksClick = useCallback(() => {
		dispatch(pushAppModal(AppModal.GenerateMeetingLinks))
	}, [dispatch])

	const handleLogClick = useCallback(() => {
		dispatch(pushAppModal(AppModal.Log))
	}, [dispatch])

	const handleDatabaseClick = useCallback(() => {
		dispatch(pushAppModal(AppModal.Database))
	}, [dispatch])

	const handleApiTokenClick = useCallback(() => {
		dispatch(pushAppModal(AppModal.DevToken))
	}, [dispatch])

	const handleDestroyEncountersClick = useCallback(async () => {
		// uh, no point in saving now!
		dispatch(setAppContent({type: RootRouteContentType.Dashboard}))
		dispatch(splode({localOnly: false}))
	}, [dispatch])

	const handleOpenStorybookClick = useCallback(() => {
		window.location.href = 'https://static.fusion-advantage.com/storybook/index.html'
	}, [])

	const handleLogoutClick = useCallback(async () => {
		// must be online for logout
		if(simulateOffline) {
			if(!confirm('Currently simulating offline— restore connectivity and logout?')) {
				return
			}
			dispatch(setSimulateOffline(false))
		}
		dispatch(saveAndReturnToDashboard())
		dispatch(logout())
	}, [simulateOffline, dispatch])

	return (
		<AppSideNavRoot ref={ref}>
			<AppSideNavContent>
				<NavList>
					{displayIdentity && !isPreviewMode ? (
						<li>
							<NavItemButton onClick={handleDashboardClick}>
								<NavItemButtonIcon>
									<FontAwesomeIcon fixedWidth icon={faDashboard} />
								</NavItemButtonIcon>
								Dashboard
							</NavItemButton>
						</li>
					): null}
					{displayIdentity && !isPreviewMode ? (
						isTrainingMode ? (
							<li>
								<NavItemButton onClick={handleTrainingModeClick}>
									<NavItemButtonIcon>
										<FontAwesomeIcon fixedWidth icon={faPersonRunningFast} />
									</NavItemButtonIcon>
									Not Training Mode
								</NavItemButton>
							</li>
						) : (
							<li>
								<NavItemButton onClick={handleTrainingModeClick}>
									<NavItemButtonIcon>
										<FontAwesomeIcon fixedWidth icon={faTricycle} />
									</NavItemButtonIcon>
									Training Mode
								</NavItemButton>
							</li>
						)
					) : null}
					{(!isTrainingMode && !isPreviewMode && canViewAdhocEncounters(authedIdentity)) ? (
						<li>
							<NavItemButton onClick={handleManageEncountersClick}>
								<NavItemButtonIcon>
									<FontAwesomeIcon fixedWidth icon={faMagnifyingGlass} />
								</NavItemButtonIcon>
								Manage Encounters
							</NavItemButton>
						</li>
					) : null}
					{(!isTrainingMode && !isPreviewMode && authedIdentity && authedIdentity.roles.includes(UserRole.VIEW_ADHOC_ENCOUNTERS)) ? (
						<li>
							<NavItemButton onClick={handleGenerateMeetingLinksClick}>
								<NavItemButtonIcon>
									<FontAwesomeIcon fixedWidth icon={faLink} />
								</NavItemButtonIcon>
								Generate Meeting Links
							</NavItemButton>
						</li>
					) : null}
					{proposeOpenLog(authedIdentity).isOk() ? (
						<li>
							<NavItemButton onClick={handleLogClick}>
								<NavItemButtonIcon>
									<FontAwesomeIcon fixedWidth icon={faSquareTerminal} />
								</NavItemButtonIcon>
								Log
							</NavItemButton>
						</li>
					) : null}
					{proposeOpenLog(authedIdentity).isOk() ? (
						<li>
							<NavItemButton onClick={handleDatabaseClick}>
								<NavItemButtonIcon>
									<FontAwesomeIcon fixedWidth icon={faDatabase} />
								</NavItemButtonIcon>
								Database
							</NavItemButton>
						</li>
					) : null}
					{!isPreviewMode && proposeRequestApiToken(authedIdentity).isOk() ? (
						<li>
							<NavItemButton onClick={handleApiTokenClick}>
								<NavItemButtonIcon>
									<FontAwesomeIcon fixedWidth icon={faKey} />
								</NavItemButtonIcon>
								API Token
							</NavItemButton>
						</li>
					) : null}
					{displayIdentity && !isPreviewMode && proposeSplode(env, isTrainingMode).isOk() ? (
						<li>
							<NavItemButton onClick={handleDestroyEncountersClick}>
								<NavItemButtonIcon>
									<FontAwesomeIcon fixedWidth icon={faFaceExplode} />
								</NavItemButtonIcon>
								#splode
							</NavItemButton>
						</li>
					) : null}
					{(!Capacitor.isNativePlatform()) && proposeOpenStorybook(authedIdentity, env).isOk() ? (
						<li>
							<NavItemButton onClick={handleOpenStorybookClick}>
								<NavItemButtonIcon>
									<FontAwesomeIcon fixedWidth icon={faBookBlank} />
								</NavItemButtonIcon>
								Storybook
							</NavItemButton>
						</li>
					) : null}
					{displayIdentity && !isPreviewMode ? (
						<li>
							<NavItemButton onClick={handleLogoutClick}>
								<NavItemButtonIcon>
									<FontAwesomeIcon fixedWidth icon={faRightFromBracket} />
								</NavItemButtonIcon>
								Logout
							</NavItemButton>
						</li>
					) : null}
				</NavList>
			</AppSideNavContent>
			<AppSideNavFooter>
				<NavList />
				{env ? <div>API {env}</div> : null}
				{getApiDomain() ? <div>Domain {getApiDomain()}</div> : null}
				{getBuildNumber() ? <div>Build {getBuildNumber()}</div> : null}
				{getJsBundle() ? <div>Bundle {getJsBundle()}</div> : null}
				{contentType === RootRouteContentType.Form ? (
					<>
						<div>Branch {branch ? branch.substring(0, 9) : '(null)'}</div>
						<div>Renderer {renderer ?? 'Built-in'}</div>
					</>
				 ) : null}
				<div>{getCopyright()}</div>
			</AppSideNavFooter>
		</AppSideNavRoot>
	)
})

export default L4AppSideNav
